import { ValidationObserver } from 'vee-validate';
import { Vue, Component, Prop, Watch, Ref } from 'vue-property-decorator';
import { State as StateClass, Getter, Action } from 'vuex-class';
import { MetaInfo } from 'vue-meta';
import { ADD_TOAST_MESSAGE as addToastMessage } from 'vuex-toast';
import { IdentCheckoutRouteNames } from '@/helpers/checkout';
import { IdentRouteNames } from '@/modules/identification/identRouteNames';
import QuestionnaireComponent from '@/components/common/questionnaire/Questionnaire.vue';
import Questionnaire from '@/components/common/questionnaire/Questionnaire';
import { State } from '@/store/models';
import Navbar from '@/components/common/navbar/Navbar.vue';
import { titleTemplate } from '@/helpers/meta/title-template';
import { IdentificationRequestStatus } from '@/store/models/identificationRequest';
import CheckoutHeader from '@/components/checkout/header/CheckoutHeader.vue';
import FormStatus from './form/IdentificationForm.vue';
import { requireQuestionnaire } from '../../../../whitelabel.config';

type Toaster = { text: string, type: 'info' | 'success' | 'warning' | 'danger', dismissAfter: number };

@Component({
  components: {
    CheckoutHeader,
    FormStatus,
    Navbar,
    QuestionnaireComponent,
    ValidationObserver,
  },
})
export default class Identification extends Vue {
  metaInfo(): MetaInfo {
    return {
      title: titleTemplate(this.$t('meta.identification.title').toString()),
    };
  }

  @Prop({ default: true })
  header!: boolean; // indicates that we're not in the checkout flow
  @Ref('form') readonly form!: InstanceType<typeof ValidationObserver>;
  @Ref('questionnaireRef') readonly questionnaireRef!: Questionnaire;

  @StateClass user!: State['user'];
  @StateClass identificationRequest!: State['identificationRequest'];
  @StateClass operations!: State['operations'];

  @Action(addToastMessage) addToastMessage!: (arg0: Toaster) => any;

  @Getter isInvestor!: boolean;

  IdentRouteNames = IdentRouteNames;

  get isMainRoute(): boolean {
    return this.$route.matched.length === 1 || this.$route.name === IdentCheckoutRouteNames.MAIN;
  }

  get showFormStatus(): boolean {
    return (!this.isInvestor && !!this.identificationRequest) || (this.identificationStatus === IdentificationRequestStatus.Approved &&
      (this.$route.name !== IdentRouteNames.IDIN && this.$route.name !== IdentCheckoutRouteNames.IDIN)); // on IDIN we show the IDIN success screen
  }

  get showQuestionnaire(): boolean {
    return !this.user?.questionnaire && requireQuestionnaire;
  }

  get sendingForm(): boolean {
    return this.operations.name === 'SEND_PRIVATE_IDENTIFICATION_PROCESSING' || this.operations.name === 'SEND_BUSINESS_IDENTIFICATION_PROCESSING';
  }

  get identificationStatus(): string {
    if (this.user && this.isInvestor) {
      return IdentificationRequestStatus.Approved;
    }

    const customIdent = this.identificationRequest;
    if (customIdent) {
      if (customIdent.status === IdentificationRequestStatus.Initial) {
        return 'pending';
      }
      if (customIdent.status === IdentificationRequestStatus.Rejected) {
        return IdentificationRequestStatus.Rejected;
      }
    }

    return IdentificationRequestStatus.Initial;
  }

  get redirect(): any {
    return this.$route.query.redirect;
  }

  get from(): any {
    return this.$route.query.from;
  }

  async submitQuestionnaire(): Promise<void> {
    const valid = await this.form.validate();
    if (!valid) {
      this.addToastMessage({ dismissAfter: 1000, text: 'Form contains invalid fields', type: 'danger' });
      return;
    }
    await this.questionnaireRef.submit();
  }

  goBack(): void {
    this.$router.push(this.from || '/account/settings/identification')
        .catch((): void => {});
  }

  goTo(identName: IdentRouteNames): void {
    const query: { [key: string]: any } = {};
    if (this.redirect) {
      query.redirect = this.redirect;
    }
    if (this.from) {
      query.from = this.from;
    }
    this.$router.push({
      name: identName,
      query,
    }).catch((): void => {});
  }
}
